/**
 * Block 12 PR-H5b Commit 1 (ADR-0032) — Container Экрана «Сохранённые КП»
 * (ADR-0005 Container/Presenter).
 *
 * Orchestrates:
 *   - Mount-time fetch через `listSavedQuotes()` (full list — no pagination,
 *     mirror ContractListContainer / B-156).
 *   - Sort by `updatedAt DESC` — newest first per UX expectation.
 *   - 4-state machine (loading → success/empty/error) maps к SavedQuotesListView
 *     P-021 states.
 *   - Retry callback (`attempt` bump) regenerates fetch.
 *   - Select → navigate `/calculator/quote?savedQuoteId=<id>` (Commit 2
 *     QuotePageContainer hydration consume; этот PR только browse + landing).
 *
 * **MUST NOT** менять savedQuotesIdb.ts — этот модуль только consumer.
 *
 * Reference:
 *   - `apps/web/src/features/contract/ContractListContainer.tsx` (эталон паттерна)
 *   - ADR-0032 (SavedQuotesList browse screen)
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { SavedQuotesListView } from './SavedQuotesListView';
import { deleteSavedQuote, listSavedQuotes, type SavedQuote } from './state/savedQuotesIdb';

type LoadState =
  | { status: 'loading' }
  | { status: 'success'; quotes: SavedQuote[] }
  | { status: 'empty' }
  | { status: 'error' };

export function SavedQuotesListContainer() {
  const navigate = useNavigate();
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });

    listSavedQuotes()
      .then((quotes) => {
        if (cancelled) return;
        if (quotes.length === 0) {
          setState({ status: 'empty' });
          return;
        }
        const sorted = [...quotes].sort((a, b) => b.updatedAt - a.updatedAt);
        setState({ status: 'success', quotes: sorted });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (import.meta.env.DEV) {
          // P-009 — логируем только err.name, не payload (может содержать ПДн).
          const errName = err instanceof Error ? err.name : 'unknown';
          console.error('[SavedQuotesListContainer] listSavedQuotes failed', errName);
        }
        setState({ status: 'error' });
      });

    return () => {
      cancelled = true;
    };
  }, [attempt]);

  const handleSelect = useCallback(
    (id: string) => {
      navigate('/calculator/quote?savedQuoteId=' + id);
    },
    [navigate],
  );

  const handleRetry = useCallback(() => setAttempt((n) => n + 1), []);

  // PR-H5c — delete КП flow. После confirm в DeleteSavedQuoteDialog (rendered
  // внутри SavedQuoteCard) → deleteSavedQuote(id) → refetch list через attempt
  // bump (mirror ContractListContainer R7-#2 pattern). Error: log err.name в
  // DEV (P-009 — не payload) + refetch anyway чтобы list синхронизировался с
  // IDB (P-021).
  const handleDelete = useCallback(async (id: string): Promise<void> => {
    try {
      await deleteSavedQuote(id);
      setAttempt((n) => n + 1);
    } catch (err) {
      if (import.meta.env.DEV) {
        const errName = err instanceof Error ? err.name : 'unknown';
        console.error('[SavedQuotesListContainer] deleteSavedQuote failed', errName);
      }
      setAttempt((n) => n + 1);
    }
  }, []);

  const quotes = state.status === 'success' ? state.quotes : undefined;

  return (
    <SavedQuotesListView
      status={state.status}
      quotes={quotes}
      onSelect={handleSelect}
      onDelete={handleDelete}
      onRetry={handleRetry}
    />
  );
}
